import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { withBuildingScope } from "../ui/building-scope";
import { DateOnlyInput } from "../components/DateOnlyInput";
import {
  formatDateOnly,
  formatDateTime,
  timestampDateOnlyValue,
} from "../dates";

type State = {
  writesEnabled: boolean;
  methodRevision: string;
  reportVersion: number;
  status: string;
  submissionId: string | null;
  routing: any[];
  checks: any[];
  reviews: any[];
  next: { name: string | null; stage: string } | null;
  canConfigure: boolean;
  canCheck: boolean;
  canFinalReview: boolean;
  latestCheckId: string | null;
};
export function VisualInspectionReview({
  id,
  building,
  version,
  disabled,
  onSaved,
  onEvidence,
}: {
  id: string;
  building: string;
  version: number;
  disabled: boolean;
  onSaved: () => Promise<unknown>;
  onEvidence: (photos: boolean) => void;
}) {
  const [data, setData] = useState<State>(),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false),
    [pendingView, setPendingView] = useState(false),
    [options, setOptions] = useState<{
      coordinators: { id: string; name: string }[];
      finalReviewers: { id: string; name: string }[];
    }>();
  const [coordinator, setCoordinator] = useState(""),
    [reviewer, setReviewer] = useState(""),
    [starts, setStarts] = useState(timestampDateOnlyValue(new Date())),
    [ends, setEnds] = useState(""),
    [basis, setBasis] = useState(""),
    [note, setNote] = useState("");
  const [flags, setFlags] = useState({
    coverageChecked: false,
    evidenceChecked: false,
    previousFindingsChecked: false,
  });
  const pending = useRef<
      | {
          path: string;
          payload: string;
          key: string;
          confirmed: boolean;
          success: string;
        }
      | undefined
    >(undefined),
    inFlight = useRef(false);
  const path = withBuildingScope(
    "/api/field-reports/" + id + "/review-stage",
    building,
  );
  const load = async () => {
    const next = await api<State>(path);
    setData(next);
    return next;
  };
  useEffect(() => {
    let active = true;
    api<State>(path)
      .then((next) => {
        if (active) setData(next);
      })
      .catch((e) => {
        if (active) setMessage(e.message);
      });
    return () => {
      active = false;
    };
  }, [path, version]);
  useEffect(() => {
    if (!data?.canConfigure) return;
    let active = true;
    api<any>(
      withBuildingScope(
        "/api/field-reports/" + id + "/review-routing-options",
        building,
      ),
    )
      .then((next) => {
        if (active) {
          setOptions(next);
          setCoordinator(data.routing[0]?.coordinator_account_id ?? "");
          setReviewer(data.routing[0]?.final_reviewer_account_id ?? "");
          setStarts(
            data.routing[0]?.starts_on ?? timestampDateOnlyValue(new Date()),
          );
          setEnds(data.routing[0]?.ends_on ?? "");
        }
      })
      .catch((e) => {
        if (active) setMessage(e.message);
      });
    return () => {
      active = false;
    };
  }, [data?.canConfigure, data?.routing[0]?.id, id, building]);
  async function resumePending() {
    if (inFlight.current || !pending.current) return;
    inFlight.current = true;
    setBusy(true);
    setMessage("");
    try {
      const saved = pending.current;
      if (!saved.confirmed) {
        try {
          await api(
            withBuildingScope(
              "/api/field-reports/" + id + saved.path,
              building,
            ),
            {
              method: "POST",
              body: JSON.stringify({
                ...JSON.parse(saved.payload),
                idempotencyKey: saved.key,
              }),
            },
          );
          saved.confirmed = true;
        } catch (e: any) {
          if (e?.statusCode >= 400 && e?.statusCode < 500) {
            pending.current = undefined;
            setPendingView(false);
          }
          throw e;
        }
      }
      await onSaved();
      await load();
      pending.current = undefined;
      setPendingView(false);
      setNote("");
      setFlags({
        coverageChecked: false,
        evidenceChecked: false,
        previousFindingsChecked: false,
      });
      setMessage(saved.success);
    } catch (e: any) {
      setMessage(
        e.message ?? "Review could not be saved. Your entries have been kept.",
      );
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }
  async function send(
    suffix: string,
    body: Record<string, unknown>,
    success: string,
  ) {
    if (inFlight.current) return;
    if (pending.current) {
      setMessage(
        "Retry the previous review action before starting another change.",
      );
      return;
    }
    pending.current = {
      path: suffix,
      payload: JSON.stringify({
        expectedVersion: data!.reportVersion,
        ...body,
      }),
      key: crypto.randomUUID(),
      confirmed: false,
      success,
    };
    setPendingView(true);
    await resumePending();
  }
  const locked = disabled || busy || pendingView,
    current = data?.routing[0];
  return (
    <section
      className="visual-inspection-review report-no-print"
      aria-label="Inspection review"
    >
      <h2>Inspection review</h2>
      {message && <p role="status">{message}</p>}
      {pendingView && !busy && (
        <>
          <p>
            The previous review action or its reload is not yet confirmed. Retry
            uses the original entries and request key.
          </p>
          <button type="button" onClick={() => void resumePending()}>
            Retry review action
          </button>
        </>
      )}
      {!data && !message && <p role="status">Loading review stages…</p>}
      {data && (
        <>
          <p>
            <strong>
              {data.next?.stage ?? "Review routing needs attention"}
            </strong>
            {data.next?.name ? " · " + data.next.name : ""}
          </p>
          {!data.writesEnabled && (
            <p>
              Inspection review changes are not enabled. Retained review history
              is read-only.
            </p>
          )}
          {current ? (
            <p>
              Coordinator: {current.coordinator_name} · Final reviewer:{" "}
              {current.final_reviewer_name}
              <br />
              Review team version {current.revision} ·{" "}
              {formatDateOnly(current.starts_on)} to{" "}
              {formatDateOnly(current.ends_on)} (end date not included)
              {!current.current &&
                " · Current duties, identity or review period need attention."}
            </p>
          ) : (
            <p>
              A Property Manager must designate the review team before this
              report can be checked.
            </p>
          )}
          <div className="report-actions">
            <button
              type="button"
              disabled={locked}
              onClick={() => onEvidence(false)}
            >
              Review submitted observations
            </button>
            <button
              type="button"
              disabled={locked}
              onClick={() => onEvidence(true)}
            >
              Review photographs
            </button>
          </div>
          {data.canConfigure && (
            <details>
              <summary>Set the review team</summary>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void send(
                    "/review-routing",
                    {
                      id: crypto.randomUUID(),
                      priorId: current?.id ?? null,
                      coordinatorAccountId: coordinator,
                      finalReviewerAccountId: reviewer,
                      startsOn: starts,
                      endsOn: ends,
                      methodRevision: data.methodRevision,
                      basis,
                    },
                    "Review team saved. Any previous completeness check must be repeated for this team.",
                  );
                }}
              >
                <fieldset disabled={locked}>
                  <legend>Building review team</legend>
                  <label>
                    Operations Coordinator
                    <select
                      aria-label="Operations Coordinator"
                      required
                      value={coordinator}
                      onChange={(e) => setCoordinator(e.target.value)}
                    >
                      <option value="">Choose eligible coordinator</option>
                      {options?.coordinators.map((u) => (
                        <option value={u.id} key={u.id}>
                          {u.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Final Property Manager reviewer
                    <select
                      aria-label="Final Property Manager reviewer"
                      required
                      value={reviewer}
                      onChange={(e) => setReviewer(e.target.value)}
                    >
                      <option value="">Choose eligible reviewer</option>
                      {options?.finalReviewers.map((u) => (
                        <option value={u.id} key={u.id}>
                          {u.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="report-grid">
                    <label>
                      Start date
                      <DateOnlyInput
                        required
                        value={starts}
                        onChange={(v) => setStarts(v ?? "")}
                      />
                    </label>
                    <label>
                      End date (not included)
                      <DateOnlyInput
                        required
                        value={ends}
                        onChange={(v) => setEnds(v ?? "")}
                      />
                    </label>
                  </div>
                  <label>
                    Why this review team applies
                    <textarea
                      required
                      minLength={3}
                      maxLength={4000}
                      value={basis}
                      onChange={(e) => setBasis(e.target.value)}
                    />
                  </label>
                  <p>
                    This designates existing eligible people. Cover must already
                    have explicit authority; no roles or building access are
                    granted here.
                  </p>
                  <button disabled={!coordinator || !reviewer || !ends}>
                    Save review team
                  </button>
                </fieldset>
              </form>
            </details>
          )}
          {data.canCheck && (
            <fieldset disabled={locked}>
              <legend>Coordinator completeness check</legend>
              {(
                [
                  ["coverageChecked", "Coverage and uninspected areas checked"],
                  [
                    "evidenceChecked",
                    "Photos and safety/access exceptions checked",
                  ],
                  ["previousFindingsChecked", "Previous findings checked"],
                ] as const
              ).map(([key, label]) => (
                <label className="report-toggle" key={key}>
                  <input
                    type="checkbox"
                    checked={flags[key]}
                    onChange={(e) =>
                      setFlags({ ...flags, [key]: e.target.checked })
                    }
                  />
                  {label}
                </label>
              ))}
              <label>
                Coordinator notes (10+ characters to return)
                <textarea
                  maxLength={4000}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </label>
              <p>
                Passing leaves the report submitted for PM review. It does not
                accept the report or resolve findings.
              </p>
              <div className="report-actions">
                <button
                  type="button"
                  disabled={!Object.values(flags).every(Boolean)}
                  onClick={() =>
                    void send(
                      "/completeness",
                      {
                        id: crypto.randomUUID(),
                        submissionId: data.submissionId,
                        routingId: current.id,
                        outcome: "Passed",
                        ...flags,
                        note,
                      },
                      "Completeness check passed. The report remains submitted for PM review.",
                    )
                  }
                >
                  Pass completeness check
                </button>
                <button
                  type="button"
                  disabled={note.trim().length < 10}
                  onClick={() =>
                    void send(
                      "/completeness",
                      {
                        id: crypto.randomUUID(),
                        submissionId: data.submissionId,
                        routingId: current.id,
                        outcome: "Returned",
                        ...flags,
                        note,
                      },
                      "Report returned for correction. The submission and coordinator check are retained.",
                    )
                  }
                >
                  Return from coordinator check
                </button>
              </div>
            </fieldset>
          )}
          {data.canFinalReview && (
            <fieldset disabled={locked}>
              <legend>Final Property Manager review</legend>
              <p>
                The latest coordinator check applies to this submission and
                review team. Acceptance records review of the observations;
                findings remain in their original workflows.
              </p>
              <label>
                Final review reason (10+ characters to return)
                <textarea
                  maxLength={4000}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </label>
              <div className="report-actions">
                {(["Accepted", "Returned"] as const).map((outcome) => (
                  <button
                    type="button"
                    key={outcome}
                    disabled={outcome === "Returned" && note.trim().length < 10}
                    onClick={() =>
                      void send(
                        "/review",
                        {
                          submissionId: data.submissionId,
                          routingId: current.id,
                          checkId: data.latestCheckId,
                          outcome,
                          reason: note,
                        },
                        outcome === "Accepted"
                          ? "Report accepted. Findings remain in their original workflows."
                          : "Report returned for correction. This review and earlier evidence are retained.",
                      )
                    }
                  >
                    {outcome === "Accepted"
                      ? "Accept report"
                      : "Return for correction"}
                  </button>
                ))}
              </div>
            </fieldset>
          )}
          <details>
            <summary>Review history</summary>
            {data.routing.map((r) => (
              <article key={r.id}>
                <h3>Review team version {r.revision}</h3>
                <p>
                  {r.coordinator_name} · {r.final_reviewer_name} ·{" "}
                  {formatDateOnly(r.starts_on)} to {formatDateOnly(r.ends_on)}{" "}
                  (end date not included)
                </p>
                <p>{r.basis}</p>
              </article>
            ))}
            {!data.checks.length && !data.reviews?.length && (
              <p>No review checks have been recorded.</p>
            )}
            {data.checks.map((c) => (
              <article key={c.id}>
                <h3>
                  Coordinator check · {c.outcome} · Submission{" "}
                  {c.submission_revision}
                </h3>
                <p>
                  Review team version {c.routing_revision} · Coverage{" "}
                  {c.coverage_checked ? "checked" : "not confirmed"} · Evidence{" "}
                  {c.evidence_checked ? "checked" : "not confirmed"} · Previous
                  findings{" "}
                  {c.previous_findings_checked ? "checked" : "not confirmed"}
                </p>
                <p>
                  {c.actor_name} · {formatDateTime(c.recorded_at)}
                </p>
                <p>{c.note}</p>
              </article>
            ))}
            {data.reviews?.map((r) => (
              <article key={r.id}>
                <h3>
                  {r.profile_stage} · {r.outcome} · Submission{" "}
                  {r.submission_revision}
                </h3>
                <p>
                  {r.reviewer_name} · {formatDateTime(r.reviewed_at)}
                </p>
                <p>{r.reason}</p>
              </article>
            ))}
          </details>
        </>
      )}
    </section>
  );
}
